import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const APP_NAME = 'Tracking App';

interface TitleApi {
  /** Name of the matched route, supplied by the layout. */
  setRouteTitle: (title: string | null) => void;
  /** More specific title owned by a screen; wins over the route name. */
  setScreenTitle: (title: string | null) => void;
}

const noop = () => {};

const DocumentTitleContext = createContext<TitleApi>({
  setRouteTitle: noop,
  setScreenTitle: noop,
});

/**
 * Single owner of `document.title`.
 *
 * The layout and the screen both have something to say about the title, and
 * letting each write directly makes the result depend on effect ordering — a
 * child's effect runs before its parent's, so the layout would clobber the
 * screen on mount but lose to it on every later update. Collecting both here
 * keeps one writer and a fixed precedence.
 */
export const DocumentTitleProvider: React.FC = ({ children }) => {
  const [routeTitle, setRouteTitle] = useState<string | null>(null);
  const [screenTitle, setScreenTitle] = useState<string | null>(null);

  useEffect(() => {
    const title = screenTitle || routeTitle;
    document.title = title ? `${title} · ${APP_NAME}` : APP_NAME;
  }, [routeTitle, screenTitle]);

  // Both setters are stable, so the value must be too. A fresh object here would
  // re-render every consumer on each title change — including MainLayout, which
  // remounts the whole active route when it renders.
  const api = useMemo(() => ({ setRouteTitle, setScreenTitle }), []);

  return (
    <DocumentTitleContext.Provider value={api}>
      {children}
    </DocumentTitleContext.Provider>
  );
};

/** Title fallback derived from the route table. Call once, from the layout. */
export const useRouteTitle = (title?: string | null) => {
  const { setRouteTitle } = useContext(DocumentTitleContext);

  useEffect(() => {
    setRouteTitle(title || null);
  }, [title, setRouteTitle]);
};

/**
 * Give the current screen a tab title of its own. Cleared on unmount so a title
 * never leaks onto the next screen.
 */
export const useDocumentTitle = (title?: string | null) => {
  const { setScreenTitle } = useContext(DocumentTitleContext);

  useEffect(() => {
    setScreenTitle(title || null);
    return () => setScreenTitle(null);
  }, [title, setScreenTitle]);
};

export default DocumentTitleProvider;
